import type { DsdEditorValueOccurrence } from "@/apis/dsd-editor/route";
import type { DsdEditorParsedItem } from "@/utils/dsd-editor/document-items";
import { getOccurrenceIds } from "@/utils/dsd-editor/note-reference-review";

export type DsdReviewEvidenceView = {
  sections: string[];
  itemsBySection: ReadonlyMap<string, DsdEditorParsedItem[]>;
  /** 후보 값이 든 셀 — 테두리 강조 */
  cellIds: ReadonlySet<string>;
  /** 그 셀이 속한 행 전체 — 채움 강조 (행 선택 결과) */
  rowCellIds: ReadonlySet<string>;
};

/**
 * 근거 창에 그릴 것: 후보가 든 표와 그 앞 설명문(이전 표 이후의 문단들). 후보가 문단 값이면 같은 규칙으로 이전 표 이후의 문단부터 그 문단까지.
 * 청크 안에서 후보 셀을 가진 아이템을 찾고, 못 찾으면(셀 id 를 못 맞춘 경우) 청크 전체로 물러선다.
 * 백엔드는 청크 단위라 여기서 좁힌다
 */
export const getDsdReviewEvidenceView = (
  itemsBySection: ReadonlyMap<string, DsdEditorParsedItem[]>,
  occurrence: DsdEditorValueOccurrence,
): DsdReviewEvidenceView => {
  const ids = new Set(getOccurrenceIds(occurrence));
  const chunk = new Map<string, DsdEditorParsedItem[]>();
  let hit: { section: string; items: DsdEditorParsedItem[] } | null = null;
  const rowCellIds = new Set<string>();

  for (const [section, items] of itemsBySection) {
    const chunkItems = items.filter(
      (item) => item.chunkId === occurrence.chunkId,
    );
    if (chunkItems.length === 0) continue;
    chunk.set(section, chunkItems);
    if (hit) continue;
    // 이전 표 다음부터 후보가 든 아이템까지 — 표 앞 설명문을 같이 보여준다
    let start = 0;
    for (const [index, item] of chunkItems.entries()) {
      if (item.kind === "table") {
        const row = item.table.rows.find((row) =>
          row.cells.some((cell) => ids.has(cell.cellId)),
        );
        if (!row) {
          start = index + 1;
          continue;
        }
        for (const cell of row.cells) rowCellIds.add(cell.cellId);
        hit = { section, items: chunkItems.slice(start, index + 1) };
        break;
      }
      if (item.valueItemId && ids.has(item.valueItemId)) {
        hit = { section, items: chunkItems.slice(start, index + 1) };
        break;
      }
    }
  }

  const scoped: ReadonlyMap<string, DsdEditorParsedItem[]> = hit
    ? new Map([[hit.section, hit.items]])
    : chunk;
  return {
    sections: [...scoped.keys()],
    itemsBySection: scoped,
    cellIds: ids,
    rowCellIds,
  };
};
